<script setup lang="ts">
import { storeToRefs } from "pinia";
import type { Ref } from "vue";
import { useAcl } from "vue-simple-acl";
import { SearchMode } from "~/core/composables/useSearch";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import LxcError from "~/core/models/LxcError.class";
import { versionCompare } from "~/core/utils/versionCompare";
import { useDtwinOperationModels } from "~/modules/dtwin/composables/useDtwinOperationManager";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import { DtwinLifeCycleState } from "~/modules/dtwin/models/DtwinLifeCycleState.enum";
import type { DtwinModel } from "~/modules/dtwin/models/DtwinModel.interface";
import { DtwinOperationTypes } from "~/modules/dtwin/models/DtwinOperationTypes.enum";
import type { DynamicUIDetail } from "~/modules/dtwin/models/DynamicUIDetail.interface";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import { Filters } from "~/types";
import ILxcPlus from "~icons/lxc/plus";

const props = defineProps<{
  organizationCode: number;
  dtwin?: Dtwin;
  dtwinModel?: DtwinModel;
  uiConfigFirmware?: Record<string, Record<string, DynamicUIDetail[]>>;
}>();

const { t } = useI18n();

const { can } = useAcl();

const { currentOrganization } = storeToRefs(useOrganizationStore());

/**
 * Dtwin operation model
 */
const isLoadingDtwinOperationModel = ref(false);
const errorDtwinOperationModel = ref();
const dtwinOperationModel = ref();

const {
  error: dtwinOperationModelsError,
  fetchData: fetchDtwinOperationModels,
  results: dtwinOperationModelsResults,
  setFilter: setDtwinOperationModelsFilter,
} = useDtwinOperationModels({
  organizationCode: currentOrganization.value?.code,
  searchMode: SearchMode.FILTER_SEARCH,
});

async function fetchDtwinOperationModel() {
  if (props.dtwinModel) {
    errorDtwinOperationModel.value = undefined;

    isLoadingDtwinOperationModel.value = true;

    setDtwinOperationModelsFilter(
      Filters.DTWIN_OPERATION_MODEL_TYPE,
      DtwinOperationTypes.FIRMWARE_UPDATE,
    );
    setDtwinOperationModelsFilter(Filters.DTWIN_OPERATION_MODEL_DEVICE_TYPE, [
      props.dtwinModel.name,
    ]);
    setDtwinOperationModelsFilter(
      Filters.DTWIN_OPERATION_MODEL_HIDDEN,
      "false",
    );

    await fetchDtwinOperationModels();

    if (LxcError.check(dtwinOperationModelsError.value)) {
      errorDtwinOperationModel.value = dtwinOperationModelsError;
    } else {
      const dtwinOperationModels = dtwinOperationModelsResults.value?.results;

      // sort the operation models by version and get the last one
      dtwinOperationModel.value = dtwinOperationModels?.length
        ? dtwinOperationModels.sort(
            (dtwinOperationModel1, dtwinOperationModel2) =>
              versionCompare(
                dtwinOperationModel2.version,
                dtwinOperationModel1.version,
              ),
          )[0]
        : undefined;
    }

    isLoadingDtwinOperationModel.value = false;
  }
}
/**
 * End Dtwin operation model
 */

const updateFirmwareModalShow: Ref<boolean> = ref(false);
const reloadOperationList: Ref<boolean> = ref(false);

const canUpdateOperations: ComputedRef<boolean> = computed(() =>
  can([ACLRoles.CAN_MANAGE_FIRMWARE_OPERATION, ACLRoles.CAN_READ_FIRMWARE]),
);

const showUpdateFirmwareModal = () => {
  updateFirmwareModalShow.value = true;
};

const doReloadOperationList = () => {
  reloadOperationList.value = !reloadOperationList.value;
};

onMounted(async () => {
  await fetchDtwinOperationModel();
});
</script>

<template>
  <container-component
    :px="0"
    :py="0"
    :is-loading="isLoadingDtwinOperationModel"
    :error="errorDtwinOperationModel"
  >
    <dtwin-form-firmware-information
      :dtwin="dtwin"
      :ui-config-firmware="uiConfigFirmware"
    />

    <!-- Firmware update operations are available only if a model operation exists for the device type -->
    <block-container
      v-if="canUpdateOperations && dtwin && dtwinModel && dtwinOperationModel"
      title-top
      with-margin-bottom
    >
      <lxc-button
        type="primary"
        html-type="button"
        :icon="ILxcPlus"
        :title="t('dtwins.form.firmware.updateFirmware')"
        :disabled="dtwin.lifeCycleState === DtwinLifeCycleState.DISABLED"
        @click.prevent="showUpdateFirmwareModal"
      >
        {{ t("dtwins.form.firmware.updateFirmware") }}
      </lxc-button>
    </block-container>

    <block-container
      v-if="
        can(ACLRoles.CAN_MANAGE_FIRMWARE_OPERATION) &&
        dtwin &&
        dtwinModel &&
        dtwinOperationModel
      "
      :title="t('section.operations.title')"
      title-top
    >
      <dtwin-form-firmware-operation-list
        :organization-code="organizationCode"
        :dtwin="dtwin"
        :dtwin-model="dtwinModel"
        :dtwin-operation-model="dtwinOperationModel"
        :reload="reloadOperationList"
      />
    </block-container>

    <dtwin-form-firmware-update-modal
      v-if="canUpdateOperations && dtwin && dtwinModel && dtwinOperationModel"
      v-model:show="updateFirmwareModalShow"
      :organization-code="organizationCode"
      :dtwin="dtwin"
      :dtwin-model="dtwinModel"
      :dtwin-operation-model="dtwinOperationModel"
      @change="doReloadOperationList"
    />
  </container-component>
</template>
